import React, { useCallback, useMemo } from "react";
import { useI18n } from "@/lib/i18n";
import IT from "@/locales/it.workspace-extras.json";
import DE from "@/locales/de.workspace-extras.json";
import ES from "@/locales/es.workspace-extras.json";
import ZH from "@/locales/zh.workspace-extras.json";
import RU from "@/locales/ru.workspace-extras.json";

const MAPS = { it: IT.strings, de: DE.strings, es: ES.strings, zh: ZH.strings, ru: RU.strings };
const UI_PROPS = new Set(["children", "placeholder", "title", "aria-label", "label", "text"]);

export function translateWorkspaceText(lang, value) {
  if (typeof value !== "string") return value;
  const match = value.match(/^(\s*)(.*?)(\s*)$/s);
  const core = match?.[2] || value;
  return `${match?.[1] || ""}${MAPS[lang]?.[core] || core}${match?.[3] || ""}`;
}

export function useWorkspaceTranslation() {
  const { lang } = useI18n();
  const tr = useCallback((value) => translateWorkspaceText(lang, value), [lang]);
  return { lang, tr };
}

function localizeNode(node, tr) {
  if (typeof node === "string") return tr(node);
  if (Array.isArray(node)) return node.map((child) => localizeNode(child, tr));
  if (!React.isValidElement(node)) return node;
  const next = {};
  for (const [key, value] of Object.entries(node.props || {})) {
    if (!UI_PROPS.has(key)) continue;
    if (typeof value === "string") next[key] = tr(value);
    else if (key === "children") next[key] = localizeNode(value, tr);
  }
  return Object.keys(next).length ? React.cloneElement(node, next) : node;
}

export function LocalizedScope({ children }) {
  const { tr } = useWorkspaceTranslation();
  return useMemo(() => localizeNode(children, tr), [children, tr]);
}
